<template>
  <div class="flex justify-center">
    <div class="bulk-mail-modal bg-white modal-card">
      <header class="bulk-mail-modal__header">
        <div>
          <h2>Envoyer un mail</h2>
          <p>Rédigez une campagne immédiate ou planifiez son envoi.</p>
        </div>
        <button type="button" aria-label="Fermer" :disabled="loading" @click="$parent.close()">
          <i class="fas fa-times" />
        </button>
      </header>

      <div class="bulk-mail-modal__content p-6 space-y-6 overflow-y-auto">
      <section>
        <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Modèle</h3>
        <div class="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            v-for="option in templateOptions"
            :key="option.value"
            type="button"
            class="text-left border rounded-lg px-3 py-3 transition"
            :class="template === option.value ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 hover:border-primary-300'"
            @click="template = option.value"
          >
            <p class="font-semibold">{{ option.label }}</p>
            <p class="text-xs text-gray-500 mt-1">{{ option.description }}</p>
          </button>
        </div>
      </section>

      <section>
        <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Destinataires</h3>
        <div class="mt-3 flex flex-col sm:flex-row gap-3">
          <button
            v-for="option in scopeOptions"
            :key="option.value"
            type="button"
            class="flex-1 border rounded-lg px-3 py-2 transition"
            :class="scope === option.value ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 hover:border-primary-300'"
            @click="scope = option.value"
          >
            <span class="text-sm">{{ option.label }}</span>
          </button>
        </div>
      </section>

      <section>
        <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Date d'envoi</h3>
        <div class="mt-3 sm:max-w-xs">
          <t-datepicker
            v-model="scheduledAt"
            timepicker
            dateFormat="Y-m-d H:i:S"
            userFormat="j F, Y H:i"
            valueType="format"
            placeholder="Sélectionnez la date d'envoi…"
            clearable
            :close-on-select="false"
          />
          <p class="text-xs text-gray-500 mt-1">Laissez vide pour envoyer immédiatement.</p>
        </div>
      </section>

      <section>
        <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Objet</h3>
        <input
          type="text"
          v-model="subject"
          class="mt-2 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="Objet de votre message"
        >
      </section>

      <section>
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Message</h3>
          <button
            type="button"
            class="text-xs text-primary-600 hover:text-primary-700"
          @click="setDefaults()"
            v-if="template !== 'custom'"
          >
            Réinitialiser le texte par défaut
          </button>
        </div>
        <div class="bulk-mail-toolbar mt-2">
          <div class="bulk-mail-toolbar__group">
            <span class="bulk-mail-toolbar__label">Variables</span>
            <button type="button" class="bulk-mail-toolbar__button" title="Adresse e-mail du destinataire" @click="insertVariable(placeholderTokens.email)">
              <i class="fas fa-at" /> Identifiant
            </button>
            <button type="button" class="bulk-mail-toolbar__button" :title="`Nom de votre ${organisationLabel()}`" @click="insertVariable(placeholderTokens.domain)">
              <i class="fas fa-building" /> {{ organisationLabel() }}
            </button>
            <button type="button" class="bulk-mail-toolbar__button" title="Nom de l’application" @click="insertVariable(placeholderTokens.application)">
              <i class="fas fa-mobile-alt" /> Application
            </button>
          </div>
          <div class="bulk-mail-toolbar__group">
            <span class="bulk-mail-toolbar__label">Insérer</span>
            <button type="button" class="bulk-mail-toolbar__button" title="Lien vers Google Play (le texte sélectionné devient le libellé)" @click="insertLink('play_store', 'Google Play')">
              <i class="fab fa-google-play" /> Google Play
            </button>
            <button type="button" class="bulk-mail-toolbar__button" title="Lien vers l’App Store (le texte sélectionné devient le libellé)" @click="insertLink('app_store', 'App Store')">
              <i class="fab fa-apple" /> App Store
            </button>
            <button type="button" class="bulk-mail-toolbar__button" :disabled="!openAppUrl" title="Lien vers l’application web, ou l’app si elle est installée (le texte sélectionné devient le libellé)" @click="insertLink('open_app', openAppHost || 'la version web')">
              <i class="fas fa-link" /> Lien app web
            </button>
            <button type="button" class="bulk-mail-toolbar__button" :disabled="!openAppUrl" title="Bouton qui ouvre l’application, ou le store si elle n’est pas installée" @click="insertBlock(openAppButton())">
              <i class="fas fa-external-link-alt" /> Bouton « Ouvrir »
            </button>
            <button type="button" class="bulk-mail-toolbar__button" :disabled="!openAppUrl" title="QR code à scanner avec l’appareil photo pour ouvrir l’application" @click="insertBlock(placeholderTokens.qr_code)">
              <i class="fas fa-qrcode" /> QR code
            </button>
            <button type="button" class="bulk-mail-toolbar__button" :disabled="uploadingImage" title="Image (JPG, PNG ou GIF, 5 Mo max.)" @click="pickImage">
              <i :class="uploadingImage ? 'fas fa-spinner fa-spin' : 'fas fa-image'" /> Image
            </button>
            <input ref="imageInput" type="file" accept="image/*" class="hidden" @change="uploadImage">
          </div>
        </div>
        <div class="bulk-mail-editor" :style="{ '--mail-accent': buttonColor }">
          <div id="bulk-mail-quill-toolbar" class="bulk-mail-editor__format">
            <span class="ql-formats">
              <button type="button" class="ql-bold" title="Gras" />
              <button type="button" class="ql-italic" title="Italique" />
              <button type="button" class="ql-underline" title="Souligné" />
            </span>
            <span class="ql-formats">
              <button type="button" class="ql-header" value="3" title="Titre" />
              <button type="button" class="ql-mail-accent bulk-mail-editor__accent" title="Couleur de l’application">A</button>
              <button type="button" class="ql-size bulk-mail-editor__small" value="small" title="Petit texte gris">Aa</button>
            </span>
            <span class="ql-formats">
              <button type="button" class="ql-list" value="ordered" title="Liste numérotée" />
              <button type="button" class="ql-list" value="bullet" title="Liste à puces" />
            </span>
            <span class="ql-formats">
              <button type="button" class="ql-link" title="Lien" />
              <button type="button" class="ql-clean" title="Effacer la mise en forme" />
            </span>
          </div>
          <quill-editor
            v-if="editorReady"
            ref="bodyEditor"
            v-model="body"
            :options="quillOptions"
          />
        </div>
        <p class="text-xs text-gray-500 mt-1">
          Les variables sont remplacées pour chaque destinataire. Sélectionnez un texte avant d’insérer un lien pour en faire le libellé.
          Le bouton « Ouvrir », le QR code et les images vont chacun sur leur propre ligne.
        </p>
        <p v-if="openAppUrl" class="text-xs text-gray-500 mt-1">
          Dans vos propres mails, hors de cet écran, utilisez ce lien sur le texte de votre choix :
          <code class="select-all">{{ openAppUrl }}</code>
        </p>
      </section>

      <section>
        <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Aperçu</h3>
        <div class="mt-3 border border-gray-200 rounded-lg bg-gray-50 p-4">
          <p class="text-xs text-gray-500 uppercase tracking-wide">Objet</p>
          <p class="text-sm text-gray-800 font-medium mb-4">{{ previewSubject }}</p>
          <div
            class="text-sm text-gray-700 preview-email-content"
            v-html="previewBody"
          />
        </div>
      </section>
      </div>

      <footer class="bulk-mail-modal__footer">
        <button
          type="button"
          class="bulk-mail-modal__cancel"
          :disabled="loading"
          @click="$parent.close()"
        >
          Annuler
        </button>
        <button
          type="button"
          class="bulk-mail-modal__submit"
          :disabled="!canSubmit || loading"
          @click="submit"
        >
          <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'" />
          <span v-if="loading">{{ scheduledAt ? 'Programmation…' : 'Envoi…' }}</span>
          <span v-else>{{ scheduledAt ? 'Programmer l’envoi' : 'Envoyer maintenant' }}</span>
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import { parse, isValid } from 'date-fns'
import Quill from 'quill'
import { quillEditor } from 'vue-quill-editor'
import { http } from '@/services/api.service'

const PLACEHOLDER_CODES = {
  email: '{{email}}',
  domain: '{{domain}}',
  application: '{{application}}',
  playStore: '{{play_store}}',
  appStore: '{{app_store}}',
  openApp: '{{open_app}}',
  qrCode: '{{qr_code}}',
  image: '{{image}}'
}

const SCOPE_OPTIONS = [
  {
    value: 'all',
    label: 'Tous les utilisateurs du domaine'
  },
  {
    value: 'not_downloaded',
    label: 'Utilisateurs sans compte actif'
  },
  {
    value: 'verified',
    label: 'Utilisateurs vérifiés uniquement'
  }
]

const SCOPES_BY_TEMPLATE = {
  onboarding: ['all'],
  reminder: ['not_downloaded'],
  custom: ['all', 'not_downloaded', 'verified']
}

// Classes posées par l'éditeur ; l'API les traduit en styles inline dans le mail.
const CLASS_SMALL = 'ql-size-small'
const CLASS_ACCENT = 'mail-accent'

// Format « couleur de l'application » : un span avec une classe, sans couleur en dur, pour que
// chaque application rende le mail avec sa propre couleur.
const MAIL_ACCENT_FORMAT = 'mail-accent'
if (!Quill.imports[`formats/${MAIL_ACCENT_FORMAT}`]) {
  const Inline = Quill.import('blots/inline')
  class MailAccent extends Inline {
    // Parchment ignore la valeur d'un format porté par un <span> : il faut la déclarer, comme Quill
    // le fait pour le gras, sinon le format disparaît au chargement d'un HTML existant.
    static formats () {
      return true
    }
  }
  MailAccent.blotName = MAIL_ACCENT_FORMAT
  MailAccent.tagName = 'span'
  MailAccent.className = CLASS_ACCENT
  Quill.register(MailAccent)
}

// Un paragraphe qui ne contient qu'un bloc (bouton, QR code, image), éventuellement entouré de
// mise en forme résiduelle de l'éditeur. Même règle que SendBulkUserMail côté API.
const WRAP_OPEN = '(?:\\s|<(?:strong|b|em|i|u|s|span[^>]*)>)*'
const WRAP_CLOSE = '(?:\\s|<\\/(?:strong|b|em|i|u|s|span)>)*'

const EMAIL_STYLES = {
  h2: 'margin:24px 0 8px;font-size:20px;line-height:28px;font-weight:bold;color:#111827;',
  h3: 'margin:20px 0 6px;font-size:17px;line-height:24px;font-weight:bold;color:#111827;',
  ul: 'margin:0 0 13px;padding-left:24px;',
  ol: 'margin:0 0 13px;padding-left:24px;',
  li: 'margin:0 0 4px;'
}

export default {
  name: 'UserBulkMailModal',
  components: {
    quillEditor
  },
  data () {
    return {
      template: 'onboarding',
      scope: 'all',
      scheduledAt: '',
      subject: '',
      body: '',
      loading: false,
      uploadingImage: false,
      editorReady: false,
      // Textes par défaut servis par l'API (source unique avec l'import CSV).
      defaults: null,
      applicationUrls: {
        url_appstore: '',
        url_playstore: '',
        url_open_app: ''
      }
    }
  },
  computed: {
    application () {
      return this.$store.getters.application || {}
    },
    appInfo () {
      return this.application.app || {}
    },
    appLabel () {
      return this.appInfo.label || ''
    },
    appSlug () {
      return this.appInfo.slug || ''
    },
    domainLabel () {
      const domain = this.$store.getters.domain || {}
      return domain.label || ''
    },
    openAppUrl () {
      return (this.applicationUrls && this.applicationUrls.url_open_app) || ''
    },
    openAppHost () {
      try {
        return this.openAppUrl ? new URL(this.openAppUrl).host : ''
      } catch (e) {
        return ''
      }
    },
    qrCodeUrl () {
      if (!this.openAppUrl) {
        return ''
      }
      const base = (process.env.VUE_APP_API_URL || '').replace(/\/+$/, '')
      return `${base}/application/${process.env.VUE_APP_APPLICATION}/open-app-qr.png`
    },
    buttonColor () {
      const color = (this.appInfo.ui_color_bg || '').trim()
      return /^#[0-9a-f]{6}$/i.test(color) ? color : '#2563eb'
    },
    isTract () {
      return this.appSlug === 'tract'
    },
    quillOptions () {
      return {
        theme: 'snow',
        placeholder: 'Contenu du message',
        modules: {
          toolbar: {
            container: '#bulk-mail-quill-toolbar'
          }
        }
      }
    },
    templateOptions () {
      return [
        {
          value: 'onboarding',
          label: 'Onboarding téléchargement',
          description: 'Mail de bienvenue envoyé lors de la création d’un compte.'
        },
        {
          value: 'reminder',
          label: 'Relance téléchargements',
          description: 'Relance les utilisateurs n’ayant pas encore téléchargé l’application.'
        },
        {
          value: 'custom',
          label: 'Personnalisé',
          description: 'Rédiger un message sur mesure.'
        }
      ]
    },
    // L'onboarding s'adresse à tous, la relance à ceux qui n'ont pas activé leur compte ;
    // seul le message personnalisé laisse le choix. L'API applique la même règle.
    scopeOptions () {
      const allowed = SCOPES_BY_TEMPLATE[this.template] || SCOPES_BY_TEMPLATE.custom
      return SCOPE_OPTIONS.filter(option => allowed.includes(option.value))
    },
    previewSubject () {
      return this.replaceSubjectPlaceholders(this.subject || 'Objet à définir')
    },
    previewBody () {
      return this.renderHtmlPreview(this.body || '')
    },
    bodyText () {
      return (this.body || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    canSubmit () {
      return this.subject.trim().length > 0 && this.bodyText.length > 0
    },
    placeholderTokens () {
      return {
        email: PLACEHOLDER_CODES.email,
        domain: PLACEHOLDER_CODES.domain,
        application: PLACEHOLDER_CODES.application,
        play_store: PLACEHOLDER_CODES.playStore,
        app_store: PLACEHOLDER_CODES.appStore,
        open_app: PLACEHOLDER_CODES.openApp,
        qr_code: PLACEHOLDER_CODES.qrCode
      }
    },
    previewVariables () {
      return {
        [PLACEHOLDER_CODES.email]: 'prenom.nom@example.com',
        [PLACEHOLDER_CODES.domain]: this.domainLabel || 'Votre organisation',
        [PLACEHOLDER_CODES.application]: this.appLabel || 'Votre application'
      }
    },
    linkTargets () {
      return {
        play_store: { url: this.applicationUrls.url_playstore || '', label: 'Google Play Store' },
        app_store: { url: this.applicationUrls.url_appstore || '', label: 'Apple Store' },
        open_app: { url: this.openAppUrl, label: `Ouvrir ${this.appLabel || 'l’application'}` }
      }
    }
  },
  watch: {
    template: {
      immediate: true,
      handler (newTemplate) {
        const allowed = SCOPES_BY_TEMPLATE[newTemplate] || SCOPES_BY_TEMPLATE.custom
        if (!allowed.includes(this.scope)) {
          this.scope = allowed[0]
        }
        if (newTemplate === 'custom') {
          this.subject = ''
          this.body = ''
          return
        }
        this.setDefaults()
      }
    }
  },
  created () {
    this.fetchApplicationUrls()
    this.fetchDefaults()
  },
  mounted () {
    // Quill cherche sa barre d'outils dans le DOM à l'initialisation : on attend qu'elle y soit.
    this.editorReady = true
  },
  methods: {
    fetchApplicationUrls () {
      const slug = process.env.VUE_APP_APPLICATION
      http.get(`/application/${slug}/urls`)
        .then(({ data }) => {
          this.applicationUrls = data || this.applicationUrls
        })
        .catch(() => {
          // silent fail, the store and open-app buttons stay disabled
        })
    },
    fetchDefaults () {
      http.get('/user/mail/bulk/defaults')
        .then(({ data }) => {
          this.defaults = data || null
          // Le modèle est déjà choisi : on remplit dès que les textes arrivent, sauf saisie en cours.
          if (this.template !== 'custom' && this.bodyText === '') {
            this.setDefaults()
          }
        })
        .catch(err => {
          console.error(err)
          this.$notify({
            group: 'maviepro-error',
            text: 'Impossible de charger les textes par défaut.'
          })
        })
    },
    setDefaults () {
      const defaults = this.getTemplateDefaults()
      this.subject = defaults.subject
      this.body = defaults.body
    },
    getTemplateDefaults () {
      const template = this.defaults && this.defaults[this.template]
      return template ? { subject: template.subject || '', body: template.body || '' } : { subject: '', body: '' }
    },
    organisationLabel () {
      return this.isTract ? 'organisation syndicale' : 'CSE'
    },
    openAppButton () {
      return `{{open_app}}Ouvrir ${this.appLabel}{{/open_app}}`
    },
    replaceSubjectPlaceholders (text) {
      let result = text
      Object.entries(this.previewVariables).forEach(([placeholder, value]) => {
        result = result.split(placeholder).join(value || '')
      })
      return result
    },
    quill () {
      const editor = this.$refs.bodyEditor
      return editor && editor.quill ? editor.quill : null
    },
    insertVariable (code) {
      this.insertAtCursor(code)
    },
    // {{tag}}libellé{{/tag}} : la sélection courante devient le libellé, sinon le libellé par défaut.
    insertLink (tag, defaultLabel) {
      this.insertAtCursor(null, (selected) => `{{${tag}}}${selected || defaultLabel}{{/${tag}}}`)
    },
    // Un bloc (bouton, QR code, image) va seul sur sa ligne.
    insertBlock (snippet) {
      this.insertAtCursor(snippet, null, true)
    },
    insertAtCursor (text, buildFromSelection = null, asBlock = false) {
      const quill = this.quill()
      if (!quill) {
        return
      }

      const range = quill.getSelection(true) || { index: quill.getLength() - 1, length: 0 }
      const selected = range.length ? quill.getText(range.index, range.length) : ''
      const snippet = buildFromSelection ? buildFromSelection(selected.trim()) : text

      if (asBlock) {
        // Le bloc prend sa propre ligne et le curseur repart sur une ligne vide en dessous.
        const [line, offset] = quill.getLine(range.index)
        const lineStart = range.index - offset
        const lineLength = line ? line.length() : 1
        const lineIsEmpty = lineLength <= 1
        const at = lineIsEmpty ? lineStart : lineStart + lineLength - 1
        const inserted = `${lineIsEmpty ? '' : '\n'}${snippet}\n`
        quill.insertText(at, inserted, 'user')
        quill.setSelection(at + inserted.length, 0, 'user')
        return
      }

      if (range.length) {
        quill.deleteText(range.index, range.length, 'user')
      }
      quill.insertText(range.index, snippet, 'user')
      quill.setSelection(range.index + snippet.length, 0, 'user')
    },
    pickImage () {
      if (this.$refs.imageInput) {
        this.$refs.imageInput.value = ''
        this.$refs.imageInput.click()
      }
    },
    uploadImage (event) {
      const file = event.target && event.target.files ? event.target.files[0] : null
      if (!file || this.uploadingImage) {
        return
      }

      const formData = new FormData()
      formData.append('image', file)
      this.uploadingImage = true

      http.post('/user/mail/bulk/image', formData)
        .then(({ data }) => {
          const url = data && data.url
          if (!url) {
            throw new Error('missing url')
          }
          this.insertBlock(`${PLACEHOLDER_CODES.image}${url}{{/image}}`)
        })
        .catch(err => {
          console.error(err)
          const message = err?.response?.data?.message || 'Impossible de téléverser cette image.'
          this.$notify({
            group: 'maviepro-error',
            text: message
          })
        })
        .finally(() => {
          this.uploadingImage = false
        })
    },
    qrCodeImageHtml () {
      if (!this.qrCodeUrl) {
        return ''
      }
      return `<img src="${this.escapeHtml(this.qrCodeUrl)}" alt="QR code pour ouvrir l’application" width="180" height="180" style="display:inline-block;width:180px;height:180px;">`
    },
    hostedImageHtml (url) {
      const clean = this.decodeEntities(url || '').trim()
      if (!/^https?:\/\/\S+$/i.test(clean)) {
        return ''
      }
      return `<img src="${this.escapeHtml(clean)}" alt="" style="display:inline-block;max-width:100%;height:auto;">`
    },
    buttonHtml (labelHtml, url) {
      const style = `display:inline-block;padding:12px 24px;margin:4px 0;border-radius:8px;background-color:${this.buttonColor};color:#ffffff;font-weight:bold;text-decoration:none;`
      return `<a href="${this.escapeHtml(url)}" target="_blank" rel="noopener noreferrer" style="${style}">${labelHtml}</a>`
    },
    anchorHtml (labelHtml, url) {
      return `<a href="${this.escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${labelHtml}</a>`
    },
    // Même enchaînement que SendBulkUserMail côté API : variables, blocs, liens, styles inline.
    renderHtmlPreview (html) {
      let out = html

      Object.entries(this.previewVariables).forEach(([placeholder, value]) => {
        out = out.split(placeholder).join(this.escapeHtml(value || ''))
      })

      const openApp = this.linkTargets.open_app
      out = out.replace(new RegExp(`<p>${WRAP_OPEN}\\{\\{open_app\\}\\}([\\s\\S]*?)\\{\\{\\/open_app\\}\\}${WRAP_CLOSE}<\\/p>`, 'gi'), (_, label) => {
        const labelHtml = label.trim() || this.escapeHtml(openApp.label)
        return openApp.url ? `<p>${this.buttonHtml(labelHtml, openApp.url)}</p>` : `<p>${labelHtml}</p>`
      })
      out = out.replace(new RegExp(`<p>${WRAP_OPEN}\\{\\{qr_code\\}\\}${WRAP_CLOSE}<\\/p>`, 'gi'), () => {
        const image = this.qrCodeImageHtml()
        return image ? `<p>${image}</p>` : ''
      })
      out = out.replace(new RegExp(`<p>${WRAP_OPEN}\\{\\{image\\}\\}([\\s\\S]*?)\\{\\{\\/image\\}\\}${WRAP_CLOSE}<\\/p>`, 'gi'), (_, url) => {
        const image = this.hostedImageHtml(url.replace(/<[^>]+>/g, ''))
        return image ? `<p>${image}</p>` : ''
      })

      out = out.replace(/\{\{qr_code\}\}/gi, () => this.qrCodeImageHtml())
      out = out.replace(/\{\{image\}\}([\s\S]*?)\{\{\/image\}\}/gi, (_, url) => this.hostedImageHtml(url.replace(/<[^>]+>/g, '')))

      Object.entries(this.linkTargets).forEach(([tag, target]) => {
        out = out.replace(new RegExp(`\\{\\{${tag}\\}\\}([\\s\\S]*?)\\{\\{\\/${tag}\\}\\}`, 'gi'), (_, label) => {
          const labelHtml = label.replace(/<[^>]+>/g, '').trim() ? label : this.escapeHtml(target.label)
          return target.url ? this.anchorHtml(labelHtml, target.url) : labelHtml
        })
        out = out.replace(new RegExp(`\\{\\{${tag}\\}\\}`, 'gi'), this.escapeHtml(target.url))
      })

      Object.entries(EMAIL_STYLES).forEach(([tag, style]) => {
        out = out.replace(new RegExp(`<${tag}>`, 'gi'), `<${tag} style="${style}">`)
      })
      out = out.replace(/<span class="([^"]*)">/gi, (_, classes) => {
        const list = classes.trim().split(/\s+/)
        let style = ''
        if (list.includes(CLASS_SMALL)) {
          style += 'font-size:13px;line-height:18px;color:#6b7280;'
        }
        if (list.includes(CLASS_ACCENT)) {
          style += `color:${this.buttonColor};`
        }
        return style ? `<span style="${style}">` : '<span>'
      })

      return out
    },
    escapeHtml (value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },
    decodeEntities (value) {
      const textarea = document.createElement('textarea')
      textarea.innerHTML = value
      return textarea.value
    },
    normalizeScheduledAt () {
      if (!this.scheduledAt) {
        return null
      }

      if (this.scheduledAt instanceof Date) {
        return Number.isNaN(this.scheduledAt.getTime()) ? null : this.scheduledAt.toISOString()
      }

      if (typeof this.scheduledAt === 'string') {
        const trimmed = this.scheduledAt.trim()
        if (!trimmed) return null

        const parsed = parse(trimmed, 'yyyy-MM-dd HH:mm:ss', new Date())
        if (isValid(parsed)) {
          return parsed.toISOString()
        }
      }

      return null
    },
    submit () {
      if (!this.canSubmit || this.loading) {
        return
      }
      this.loading = true

      const payload = {
        template: this.template,
        scope: this.scope,
        subject: this.subject.trim(),
        body: this.body.trim()
      }

      const normalized = this.normalizeScheduledAt()
      if (normalized) {
        payload.scheduled_at = normalized
      }

      http.post('/user/mail/bulk', payload)
        .then(({ data }) => {
          const message = data?.message || 'Envoi programmé.'
          this.$notify({
            group: 'maviepro-success',
            text: message
          })
          this.$emit('sent', data)
          this.$parent.close()
        })
        .catch(err => {
          console.error(err)
          const message = err?.response?.data?.message || 'Erreur lors de la programmation de l’envoi.'
          this.$notify({
            group: 'maviepro-error',
            text: message
          })
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.bulk-mail-modal {
  width: 90vw;
  max-width: 1200px;
  overflow: hidden;
  border-radius: 1rem;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.2);
}

.bulk-mail-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
}

.bulk-mail-modal__header h2 {
  color: #111827;
  font-size: 1.25rem;
  font-weight: 700;
}

.bulk-mail-modal__header p {
  margin-top: 0.25rem;
  color: #64748b;
  font-size: 0.875rem;
}

.bulk-mail-modal__header > button {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border: 1px solid #d1d5db;
  border-radius: 9999px;
  color: #64748b;
}

.bulk-mail-modal__header > button:hover:not(:disabled) {
  background: #f8fafc;
  color: #111827;
}

.bulk-mail-modal__content {
  max-height: 70vh;
}

.bulk-mail-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

.bulk-mail-modal__cancel,
.bulk-mail-modal__submit {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 700;
}

.bulk-mail-modal__cancel {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #475569;
}

.bulk-mail-modal__cancel:hover:not(:disabled) {
  background: #f1f5f9;
}

.bulk-mail-modal__submit {
  background: #2563eb;
  color: #fff;
}

.bulk-mail-modal__submit:hover:not(:disabled) {
  background: #1d4ed8;
}

.bulk-mail-modal__header > button:disabled,
.bulk-mail-modal__cancel:disabled,
.bulk-mail-modal__submit:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.bulk-mail-modal__header > button:focus-visible,
.bulk-mail-modal__cancel:focus-visible,
.bulk-mail-modal__submit:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.bulk-mail-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.25rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-bottom: 0;
  border-radius: 0.5rem 0.5rem 0 0;
  background: #f8fafc;
}

.bulk-mail-toolbar__group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
}

.bulk-mail-toolbar__label {
  margin-right: 0.25rem;
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.bulk-mail-toolbar__button {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.3rem 0.6rem;
  border: 1px solid #d1d5db;
  border-radius: 9999px;
  background: #fff;
  color: #334155;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.bulk-mail-toolbar__button i {
  color: #2563eb;
}

.bulk-mail-toolbar__button:hover:not(:disabled) {
  border-color: #93c5fd;
  background: #eff6ff;
  color: #1d4ed8;
}

.bulk-mail-toolbar__button:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 1px;
}

.bulk-mail-toolbar__button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Éditeur : la barre de mise en forme Quill est collée sous la barre d'insertion. */
.bulk-mail-editor ::v-deep .ql-toolbar.ql-snow {
  border-color: #e5e7eb;
  border-radius: 0;
  background: #fff;
  font-family: inherit;
}

.bulk-mail-editor ::v-deep .ql-container.ql-snow {
  border-color: #e5e7eb;
  border-radius: 0 0 0.5rem 0.5rem;
  font-family: inherit;
  font-size: 0.875rem;
}

.bulk-mail-editor ::v-deep .ql-editor {
  min-height: 320px;
  max-height: 60vh;
  line-height: 1.5;
}

.bulk-mail-editor ::v-deep .ql-editor h2 {
  margin: 1.25rem 0 0.5rem;
  font-size: 1.25rem;
  font-weight: 700;
}

.bulk-mail-editor ::v-deep .ql-editor p {
  margin-bottom: 0.5rem;
}

.bulk-mail-editor ::v-deep .ql-editor .mail-accent {
  color: var(--mail-accent, #2563eb);
}

.bulk-mail-editor ::v-deep .ql-editor .ql-size-small {
  font-size: 0.8em;
  color: #6b7280;
}

.bulk-mail-editor__accent,
.bulk-mail-editor__small {
  font-weight: 700;
  font-size: 0.8rem;
  line-height: 24px;
  text-align: center;
}

.bulk-mail-editor__accent {
  color: var(--mail-accent, #2563eb);
}

.bulk-mail-editor__small {
  color: #6b7280;
  font-size: 0.7rem;
}

.bulk-mail-editor ::v-deep .ql-snow .ql-active.bulk-mail-editor__accent,
.bulk-mail-editor ::v-deep .ql-snow .ql-active.bulk-mail-editor__small {
  border-radius: 3px;
  background: #e0e7ff;
}

/* Le contenu de l'aperçu vient de v-html : les styles scopés ne l'atteignent qu'avec ::v-deep. */
.preview-email-content ::v-deep img {
  max-width: 100%;
}

.preview-email-content ::v-deep ul {
  list-style: disc;
}

.preview-email-content ::v-deep ol {
  list-style: decimal;
}

.preview-email-content ::v-deep p {
  margin: 13px 0;
}

/* La feuille de style globale de l'admin colore les <strong> ; un client mail ne le fait pas. */
.preview-email-content ::v-deep strong {
  color: inherit;
}

code {
  background: #edf2f7;
  padding: 0.1rem 0.35rem;
  border-radius: 0.25rem;
}
</style>
